import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageCarousel({ primaryImage, galleryImages = [] }) {
  const allImages = [primaryImage, ...galleryImages].filter(Boolean);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  // Auto-reset touch tracking
  useEffect(() => {
    if (touchEnd !== null && touchStart !== null) {
      const timer = setTimeout(() => {
        setTouchStart(null);
        setTouchEnd(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [touchEnd, touchStart]);

  if (allImages.length === 0) {
    return <div className="bg-[#F7F5F0] w-full h-full flex items-center justify-center">No images available</div>;
  }

  return (
    <div className="relative">
      {/* Main carousel */}
      <div
        ref={carouselRef}
        className="bg-[#F7F5F0] relative min-h-[60vh] md:min-h-[88vh] flex items-center justify-center p-8 md:p-16 overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Images with transition */}
        <div className="relative w-full h-full flex items-center justify-center">
          {allImages.map((image, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                idx === currentIndex ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              <img src={image} alt={`Product ${idx}`} className="w-full max-w-md aspect-square object-cover" />
            </div>
          ))}
        </div>

        {/* Navigation arrows - hidden on mobile, show on larger screens */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/30 hover:bg-black/60 text-white p-2 transition-colors hidden md:flex"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/30 hover:bg-black/60 text-white p-2 transition-colors hidden md:flex"
              aria-label="Next image"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail indicators */}
      {allImages.length > 1 && (
        <div className="flex gap-2 justify-center mt-4 px-4">
          {allImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`h-2 transition-all ${
                idx === currentIndex
                  ? "bg-black w-6"
                  : "bg-black/30 w-2 hover:bg-black/50"
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image counter */}
      {allImages.length > 1 && (
        <div className="text-center mt-3 text-xs text-black/50">
          {currentIndex + 1} / {allImages.length}
        </div>
      )}
    </div>
  );
}
